import type { RecommendationContext } from "@memorang/types";
import { buildQuickStudyLaunch } from "../adapters/quick-study-adapter";
import type {
	AxisFilters,
	ContextRecommendationResult,
	ContextRecommendedSession,
	RecommendationSelection,
} from "../contract/recommendation-context-types";
import { rankCandidates } from "../domain/candidate-ranking";
import {
	fullCells,
	matchesFilters,
	stableCellOrder,
} from "../domain/cell-model";
import {
	buildExplanationContext,
	purposeFor,
} from "../domain/explanation-context";
import { inferMastery, observedAsEffective } from "../domain/mastery-inference";
import {
	allocateDistinctFirst,
	allocateOnePerTrack,
	validateSessionRequest,
} from "../domain/session-allocation";
import { selectTargetCandidates } from "../domain/target-progression";

const SECONDS_PER_ITEM = 72;

export type RecommendationInput = {
	context: RecommendationContext;
	target: { axisId: string; valueId: number };
	preferences?: AxisFilters;
	filters?: AxisFilters;
};

export const prepareRecommendationSelection = ({
	context,
	target,
	preferences,
	filters,
}: RecommendationInput):
	| RecommendationSelection
	| Extract<ContextRecommendationResult, { status: "UNAVAILABLE" }> => {
	const progressionAxes = context.axes.filter(
		({ role }) => role === "PROGRESSION",
	);
	const progressionAxis = progressionAxes[0];
	if (
		target.axisId !== progressionAxis.id ||
		!progressionAxis.values.some(({ id }) => id === target.valueId)
	) {
		throw new RangeError("target must identify a progression-axis value");
	}
	const allCells = fullCells(context);
	const matching = allCells.filter((cell) => matchesFilters(cell, filters));
	if (matching.length === 0) {
		return {
			status: "UNAVAILABLE",
			examId: context.exam.id,
			reason: "NO_MATCHING_CELLS",
			sessions: [],
		};
	}
	const launchable = matching.filter(
		(cell) => cell.source.launch.availableItemCount > 0,
	);
	if (launchable.length === 0) {
		return {
			status: "UNAVAILABLE",
			examId: context.exam.id,
			reason: "NO_AVAILABLE_ITEMS",
			sessions: [],
		};
	}
	const effective = inferMastery(allCells, progressionAxis, context.axes);
	const targetSelection = selectTargetCandidates({
		cells: launchable,
		axes: context.axes,
		progressionAxis,
		targetValueId: target.valueId,
		effective,
	});
	const candidates = rankCandidates({
		candidates: targetSelection.candidates,
		effective,
		minimumEvidence: context.policy.minimumEvidence,
		preferences,
		axes: context.axes,
		stableOrder: stableCellOrder(context.axes),
	});
	return {
		context,
		axes: context.axes,
		candidates,
		effective,
		mode: targetSelection.mode,
	};
};

const itemCountFor = ({
	itemsPerSession,
	durationMinutes,
}: {
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
}): number =>
	itemsPerSession ??
	Math.max(
		1,
		Math.min(50, Math.floor(((durationMinutes ?? 0) * 60) / SECONDS_PER_ITEM)),
	);

export const buildSession = ({
	selection,
	cell,
	rank,
	itemsPerSession,
	durationMinutes,
}: {
	selection: RecommendationSelection;
	cell: RecommendationSelection["candidates"][number];
	rank: number;
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
}): ContextRecommendedSession => {
	const requestedItemCount = itemCountFor({ itemsPerSession, durationMinutes });
	const actualItemCount = Math.min(
		requestedItemCount,
		cell.source.launch.availableItemCount,
	);
	const effective =
		selection.effective.get(cell.source.key) ?? observedAsEffective(cell);
	const purpose = purposeFor({
		mode: selection.mode,
		cell,
		minimumEvidence: selection.context.policy.minimumEvidence,
	});
	const estimatedDurationSeconds = actualItemCount * SECONDS_PER_ITEM;
	return {
		rank,
		cellId: cell.source.key,
		coordinates: cell.coordinates,
		mode: selection.mode,
		purpose,
		reason:
			selection.mode === "MAINTENANCE"
				? "MAINTENANCE"
				: purpose === "BUILD_EVIDENCE"
					? "COVERAGE_GAP"
					: "LOWEST_MASTERY",
		...(durationMinutes ? { requestedDurationMinutes: durationMinutes } : {}),
		requestedItemCount,
		actualItemCount,
		isPartial: actualItemCount < requestedItemCount,
		secondsPerItemEstimate: SECONDS_PER_ITEM,
		estimatedDurationSeconds,
		observedMastery: cell.mastery,
		effectiveMastery: effective,
		quickStudyMeta: buildQuickStudyLaunch({
			cell,
			actualItemCount,
			estimatedDurationSeconds,
		}),
		explanationContext: buildExplanationContext({
			cell,
			context: selection.context,
			effective,
			purpose,
			requestedItemCount,
			actualItemCount,
		}),
	};
};

const buildSessions = ({
	selection,
	sessionCount,
	itemsPerSession,
	durationMinutes,
	onePerTrack,
	preferences,
}: {
	selection: RecommendationSelection;
	sessionCount: number;
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
	onePerTrack?: boolean;
	preferences?: AxisFilters;
}): ContextRecommendedSession[] => {
	const trackAxes = selection.axes.filter(({ role }) => role === "TRACK");
	const rankedCandidates = onePerTrack
		? allocateOnePerTrack({
				rankedCandidates: selection.candidates,
				trackAxes,
				sessionCount,
				preferredTrackValueIds: trackAxes[0]
					? preferences?.[trackAxes[0].id]
					: undefined,
			})
		: allocateDistinctFirst({
				rankedCandidates: selection.candidates,
				sessionCount,
			});
	return rankedCandidates.map((cell, index) =>
		buildSession({
			selection,
			cell,
			rank: index + 1,
			itemsPerSession,
			durationMinutes,
		}),
	);
};

export const selectStudyRecommendations = ({
	sessionCount,
	itemsPerSession,
	durationMinutes,
	onePerTrack,
	...input
}: RecommendationInput & {
	sessionCount: number;
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
	onePerTrack?: boolean;
}): ContextRecommendationResult => {
	validateSessionRequest({ sessionCount, itemsPerSession, durationMinutes });
	const selection = prepareRecommendationSelection(input);
	if ("status" in selection) {
		return selection;
	}
	return {
		status: "READY",
		examId: selection.context.exam.id,
		sessions: buildSessions({
			selection,
			sessionCount,
			itemsPerSession,
			durationMinutes,
			onePerTrack,
			preferences: input.preferences,
		}),
	};
};
